import {
  LOGO_GEOMETRY as g,
  LOGO_SETTINGS as settings,
} from '@canopy/brand/config'

export { LOGO_SETTINGS } from '@canopy/brand/config'

type Point = readonly [number, number]
const point = (values: readonly number[]) =>
  values.map((v) => v.toFixed(3)).join(' ')
const arc = (radius: number, sweep: number, end: Point) =>
  `A ${radius} ${radius} 0 0 ${sweep} ${point(end)}`
const smoothstep = (value: number) => {
  const t = Math.max(0, Math.min(1, value))
  return t * t * (3 - 2 * t)
}

/** The captured scale transition uses cubic-bezier(0.4, 0, 0.2, 1). */
function scaleEase(progress: number): number {
  const x = Math.max(0, Math.min(1, progress))
  let low = 0
  let high = 1
  for (let i = 0; i < 24; i++) {
    const t = (low + high) / 2
    const curveX =
      3 * (1 - t) ** 2 * t * 0.4 + 3 * (1 - t) * t * t * 0.2 + t ** 3
    if (curveX < x) low = t
    else high = t
  }
  const t = (low + high) / 2
  return 3 * (1 - t) * t * t + t ** 3
}

/** Preserve the approved weight, with a small optical lift that keeps tooth gaps open. */
export function strokeForRadius(radius: number): number {
  if (!Number.isFinite(radius) || radius < 0)
    throw new RangeError('Invalid logo radius')
  const proportional = (radius * settings.strokeWidth) / settings.baselineRadius
  const opticalLift = 0.25 * Math.exp(-radius / 20)
  return Math.min(radius * 0.1, proportional + opticalLift)
}

/** Fit the mark and its full animation inside a square at a specified display size. */
export function logoLayout(size: number) {
  if (!Number.isFinite(size) || size <= 0)
    throw new RangeError('Invalid logo size')
  const radius = size * 0.42
  const strokePixels = strokeForRadius(radius)
  const scale = (radius - strokePixels / 2) / g.centerlineRadius
  const span = size / scale
  return {
    viewBox: `${512 - span / 2} ${512 - span / 2} ${span} ${span}`,
    origin: 512 - span / 2,
    span,
    radius,
    strokePixels,
    strokeWidth: strokePixels / scale,
    hubRadius: (settings.innerRadius * radius - strokePixels / 2) / scale,
  }
}

/** Preserve the captured outline while opposite teeth move along their fixed side lines. */
export function logoPath(displacement: number): string {
  const shifted = g.caps.map((cap, index) => {
    const travel =
      displacement *
      settings.amplitude *
      settings.baselineRadius *
      (index % 2 === 0 ? 1 : -1)
    const radius = g.centerlineRadius + travel
    const sideDistance = Math.sqrt(
      (radius - g.tipCorner) ** 2 - (g.offset - g.tipCorner) ** 2,
    )
    const sideX = g.direction[0] * sideDistance + g.normal[0] * g.offset
    const sideY = g.direction[1] * sideDistance + g.normal[1] * g.offset
    const faceX = sideX - g.normal[0] * g.tipCorner + g.tipCorner
    const faceY = sideY - g.normal[1] * g.tipCorner
    const rotate = (x: number, y: number): Point => [
      512 + x * cap.axis[0] - y * cap.axis[1],
      512 + x * cap.axis[1] + y * cap.axis[0],
    ]
    return {
      ...cap,
      start: rotate(sideX, -sideY),
      upperFace: rotate(faceX, -faceY),
      lowerFace: rotate(faceX, faceY),
      end: rotate(sideX, sideY),
    }
  })
  const firstCap = shifted[0]
  if (!firstCap) return ''
  const parts = [`M ${point(firstCap.start)}`]
  shifted.forEach((cap, index) => {
    const next = shifted[(index + 1) % shifted.length]
    if (!next) return
    parts.push(
      arc(g.tipCorner, 1, cap.upperFace),
      `L ${point(cap.lowerFace)}`,
      arc(g.tipCorner, 1, cap.end),
      `L ${point(cap.sideLower)}`,
    )
    if (g.wideBaseJoin) {
      const angle = (2 * Math.PI * (index + 0.5)) / shifted.length
      const axis: Point = [Math.cos(angle), Math.sin(angle)]
      const tangent: Point = [-axis[1], axis[0]]
      const valley: Point = [
        512 + g.rootRadius * axis[0],
        512 + g.rootRadius * axis[1],
      ]
      const distance = Math.hypot(
        valley[0] - cap.sideLower[0],
        valley[1] - cap.sideLower[1],
      )
      const cross = (a: Point, b: Point) => a[0] * b[1] - a[1] * b[0]
      const boundarySpeed = cross(cap.inwardLower, axis)
      const localSide: Point = [cap.sideLower[0] - 512, cap.sideLower[1] - 512]
      const boundaryDistance =
        boundarySpeed < -1e-10
          ? -cross(localSide, axis) / boundarySpeed
          : Infinity
      const sideHandle = Math.min(distance * 0.4, boundaryDistance * 0.6)
      const valleyHandle = Math.min(
        distance * 0.3,
        g.rootRadius * Math.tan(g.sectorHalf) * 0.5,
      )
      const first = cap.sideLower.map(
        (v, i) => v + (cap.inwardLower[i] ?? 0) * sideHandle,
      )
      const second = valley.map((v, i) => v - (tangent[i] ?? 0) * valleyHandle)
      const third = valley.map((v, i) => v + (tangent[i] ?? 0) * valleyHandle)
      const fourth = next.sideUpper.map(
        (v, i) => v - (next.outwardUpper[i] ?? 0) * sideHandle,
      )
      parts.push(
        `C ${point([...first, ...second, ...valley])}`,
        `C ${point([...third, ...fourth, ...next.sideUpper])}`,
      )
    } else {
      parts.push(
        arc(g.rootCorner, 0, cap.rootLower),
        arc(g.rootRadius, 1, next.rootUpper),
        arc(g.rootCorner, 0, next.sideUpper),
      )
    }
    parts.push(`L ${point(next.start)}`)
  })
  return `${parts.join(' ')} Z`
}

const run = Math.max(
  0,
  settings.rotationPeriod * settings.rotations -
    (settings.entrance + settings.exit) / 2,
)
const exitStart = settings.entrance + run
export const LOGO_CYCLE_SECONDS = exitStart + settings.exit
export const STATIC_LOGO_PATH = logoPath(0)

/** Continuous loading starts at full speed and displacement amplitude, without ramps. */
export function loadingFrame(elapsedSeconds: number) {
  const time = Math.max(0, elapsedSeconds)
  return {
    path: logoPath(Math.sin((2 * Math.PI * time) / settings.toothPeriod)),
    angle: (360 * time) / settings.rotationPeriod,
    scale: 1 + settings.scalePercent / 100,
  }
}

/** Captured logo entrance, cruise, and exit; reserved for deliberate branding effects. */
export function logoFrame(elapsedSeconds: number) {
  const time = Math.max(0, elapsedSeconds) % LOGO_CYCLE_SECONDS
  const envelope = smoothstep(
    Math.min(
      time / settings.entrance,
      (LOGO_CYCLE_SECONDS - time) / settings.exit,
    ),
  )
  const speed = 360 / settings.rotationPeriod
  let angle: number
  if (time < settings.entrance)
    angle = (speed * time * time) / (2 * settings.entrance)
  else if (time < exitStart) angle = speed * (time - settings.entrance / 2)
  else {
    const remaining = LOGO_CYCLE_SECONDS - time
    angle =
      settings.rotations * 360 -
      (speed * remaining * remaining) / (2 * settings.exit)
  }
  const scaleEnvelope =
    time < settings.entrance
      ? scaleEase(time / settings.entrance)
      : time < exitStart
        ? 1
        : 1 - scaleEase((time - exitStart) / settings.exit)
  return {
    path: logoPath(
      envelope * Math.sin((2 * Math.PI * time) / settings.toothPeriod),
    ),
    angle,
    scale: 1 + (settings.scalePercent / 100) * scaleEnvelope,
  }
}

/** Standalone static icon, also used to rasterize launcher and favicon assets. */
export function logoSvg(
  size: number,
  outputSize = size,
  background = true,
): string {
  const layout = logoLayout(size)
  const backdrop = background
    ? `<rect x="${layout.origin}" y="${layout.origin}" width="${layout.span}" height="${layout.span}" fill="#000"/>`
    : ''
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${outputSize}" height="${outputSize}" viewBox="${layout.viewBox}"><title>Canopy</title>${backdrop}<g fill="none" stroke="#fff" stroke-width="${layout.strokeWidth}" stroke-linejoin="round" stroke-linecap="round"><path d="${STATIC_LOGO_PATH}"/><circle cx="512" cy="512" r="${layout.hubRadius}"/></g></svg>`
}
